import React, { useState, useEffect } from "react";
import { styled } from "@linaria/react";
import { useTheme, ITheme } from "src/theme-revamp";
import { css } from "@linaria/core";

const IconElement = styled.img<LinkProps>`
  width: ${(props) =>
    props.iconSize ? props.theme?.iconSize[props.iconSize] + "px" : "20px"};
  height: 18px;
  position: relative;
  user-select: none;
  -webkit-user-select: none;
  -webkit-touch-callout: none;
  margin-right: ${(props) =>
    props.iconMargin
      ? props.iconMargin
      : props.slotIcon === "start"
      ? 10
      : 0}px;
  margin-left: ${(props) =>
    props.iconMargin ? props.iconMargin : props.slotIcon === "end" ? 5 : 0}px;
  top: 4px;
  object-fit: contain;
  &.tooltip,
  &.tooltip-visible {
    width: 20px;
    height: 20px;
    left: 5px;
  }
`;

const overidePosition = css`
  top: 6px;
`;

const LinkElement = styled.a<LinkProps>`
  color: ${(props) =>
    props.disabed
      ? props.theme?.colors.grey[500]
      : props.isCheckClicked
      ? props.theme?.colors.red[600]
      : props.color
      ? props.color
      : props.theme?.colors.primary};
  font-size: ${(props) => props["fontSize"] + "px"};
  cursor: pointer;
  font-family: ${(props) => props.theme?.fontFamily || ""};
  font-weight: ${(props) => (props.fontWeight ? props.fontWeight : "bold")};
  text-decoration: none;
  line-height: 24px;
  line-height: 1.5;
  letter-spacing: -0.1px;
  margin: ${(props) =>
    typeof props.margin === "number" ? `${props.margin}px` : props.margin};
  word-wrap: break-word;
  white-space: -moz-pre-wrap !important;
  white-space: -pre-wrap;
  white-space: -o-pre-wrap;
  white-space: pre-wrap;
  white-space: normal;
  display: inline;
  @media screen and (min-width: 768px) {
    font-size: ${(props) =>
      props.fontSizeTablet
        ? props.fontSizeTablet + "px"
        : props["fontSize"] + "px"};
    font-weight: ${(props) => (props.fontWeight ? props.fontWeight : "bold")};
  }
  u {
    color: ${(props) =>
      props.disabed
        ? props.theme?.colors.grey[500]
        : props.isCheckClicked
        ? props.theme?.colors.red[600]
        : props.color
        ? props.color
        : props.theme?.colors.primary};
    border-bottom: 1px solid
      ${(props) =>
        props.disabed
          ? props.theme?.colors.grey[500]
          : props.isCheckClicked
          ? props.theme?.colors.red[600]
          : props.color
          ? props.color
          : props.theme?.colors.primary};
  }
`;

const LinkEndImage = styled.a<LinkProps>`
  position: ${(props) => (props.isEndOfLine ? "absolute" : "static")};
`;

const ButtonElement = styled.button<LinkProps>`
  border: none;
  background: none;
  cursor: pointer;
  font-size: ${(props) => props["fontSize"] + "px"};
  font-weight: ${(props) => (props.fontWeight ? props.fontWeight : "bold")};
  color: ${(props) =>
    props.disabed
      ? props.theme?.colors.grey[500]
      : props.color
      ? props.color
      : props.theme?.colors.primary};
  font-family: ${(props) => props.theme?.fontFamily || ""}!important;
  padding: 0;
  line-height: 1.5;
  &:focus,
  &:active {
    background: none;
  }
  margin: ${(props) =>
    typeof props.margin === "number" ? `${props.margin}px` : props.margin};
  word-wrap: break-word;
  white-space: -moz-pre-wrap !important;
  white-space: -pre-wrap;
  white-space: -o-pre-wrap;
  white-space: pre-wrap;
  white-space: normal;
  display: inline;

  &.tooltip-visible {
    position: relative;
  }
`;

const Underline = styled.u<LinkProps>`
  padding-bottom: 0;
  text-decoration: none;
  border-bottom: ${(props) =>
    props.hoverUnderline
      ? "unset"
      : `1px ${props.underlineStyle || "solid"} ${
          props.theme?.colors
            ? props.theme?.colors.primary
            : props.theme?.colors.grey[700]
        }`};
  word-break: break-word;
  &:hover {
    border-bottom: ${(props) =>
      props.hoverUnderline
        ? `1px ${props.underlineStyle || "solid"} ${
            props.theme?.colors
              ? props.theme?.colors.primary
              : props.theme?.colors.grey[700]
          }`
        : `1px ${props.underlineStyle || "solid"} ${
            props.theme?.colors
              ? props.theme?.colors.primary
              : props.theme?.colors.grey[700]
          }`};
  }
`;

const SpanContainer = styled.span``;

const nonUnderline = css`
  text-decoration: none;
`;

const TooltipArrow = styled.i<LinkProps>`
  border: solid ${(props) => props.theme?.colors.grey[600]};
  border-width: 0 15px 15px 0;
  display: inline-block;
  padding: 0;
  transform: rotate(-135deg);
  -webkit-transform: rotate(-135deg);
  position: absolute;
  top: 34px;
  left: 8px;
`;

export interface LinkProps {
  variant?: "link" | "button";
  text?: string;
  fontSize?: number;
  fontSizeTablet?: number;
  url?: string;
  icon?: string | undefined;
  slotIcon?: "start" | "end" | "none";
  iconSize?: "xsm" | "sm" | "md" | "lg" | "dlg";
  iconMargin?: number;
  disabed?: boolean;
  underline?: boolean;
  hoverUnderline?: boolean;
  fontWeight?: string | number | undefined;
  fontWeightTablet?: string | number | undefined;
  theme?: ITheme;
  color?: string | undefined;
  onClick?: (e: any) => void;
  margin: string | number;
  target?: string;
  rel?: string;
  isCheckClicked?: boolean;
  callback?: () => void;
  downloadCallback?: () => void;
  underlineStyle?: "solid" | "dashed" | "dotted";
  download?: string;
  iconDownload?: string;
  isClicked?: boolean;
  hasDownload?: boolean;
  className?: string;
  isEndOfLine?: boolean;
}

function Link({
  variant,
  text,
  className,
  download,
  url,
  isClicked,
  icon,
  underline,
  hoverUnderline = false,
  slotIcon,
  target = "_self",
  rel = "",
  isCheckClicked,
  callback = () => {},
  downloadCallback = () => {},
  hasDownload,
  onClick,
  iconMargin,
  ...props
}: LinkProps) {
  const theme = useTheme();
  const [clicked, setClicked] = useState<boolean | undefined>(false);

  const handleClick = (event: any) => {
    callback();
    if (isCheckClicked) {
      setClicked(true);
    }
    if (onClick) {
      onClick(event);
    }
  };

  const handleDownloadClick = (event: any) => {
    downloadCallback();
  };

  useEffect(() => {
    if (isClicked !== clicked && isCheckClicked) {
      setClicked(isClicked);
    }
  }, [isClicked, isCheckClicked, clicked]);

  return (
    <React.Fragment>
      {variant === "button" ? (
        <ButtonElement
          className={className}
          theme={theme}
          {...props}
          isCheckClicked={clicked}
          onClick={handleClick}
        >
          {icon && slotIcon === "start" && (
            <IconElement
              src={icon}
              slotIcon={slotIcon}
              theme={theme}
              iconMargin={iconMargin}
              {...props}
            ></IconElement>
          )}
          {underline ? (
            <Underline {...props} theme={theme}>
              {text}
            </Underline>
          ) : (
            text
          )}
          {icon && slotIcon === "end" && (
            <IconElement
              className={[overidePosition, className].join(" ")}
              src={icon}
              slotIcon={slotIcon}
              theme={theme}
              iconMargin={iconMargin}
              {...props}
            ></IconElement>
          )}
          {className === "tooltip-visible" && (
            <TooltipArrow theme={theme} margin={""}></TooltipArrow>
          )}
        </ButtonElement>
      ) : (
        <SpanContainer className={className}>
          {icon && !hasDownload && slotIcon === "start" && (
            <a className={nonUnderline} download={download} href={url}>
              {" "}
              <IconElement
                src={icon}
                slotIcon={slotIcon}
                theme={theme}
                iconMargin={iconMargin}
                {...props}
              ></IconElement>{" "}
            </a>
          )}
          {icon && hasDownload && slotIcon === "start" && (
            <a
              className={nonUnderline}
              onClick={handleDownloadClick}
              href="javascript:;"
            >
              {" "}
              <IconElement
                src={icon}
                slotIcon={slotIcon}
                theme={theme}
                iconMargin={iconMargin}
                {...props}
              ></IconElement>{" "}
            </a>
          )}
          <LinkElement
            fontWeight={600}
            theme={theme}
            {...props}
            isCheckClicked={clicked}
            onClick={handleClick}
            target={target}
            rel={rel}
            href={url}
          >
            {underline ? (
              <Underline
                hoverUnderline={hoverUnderline}
                {...props}
                theme={theme}
              >
                {text}
              </Underline>
            ) : (
              text
            )}
          </LinkElement>
          {icon && !hasDownload && slotIcon === "end" && (
            <LinkEndImage {...props} download={download} href={url}>
              <IconElement
                src={icon}
                slotIcon={slotIcon}
                theme={theme}
                iconMargin={iconMargin}
                {...props}
              ></IconElement>
            </LinkEndImage>
          )}
          {hasDownload && slotIcon === "end" && (
            <LinkEndImage
              {...props}
              onClick={handleDownloadClick}
              href="javascript:;"
            >
              <IconElement
                src={icon}
                slotIcon={slotIcon}
                theme={theme}
                iconMargin={iconMargin}
                {...props}
              ></IconElement>
            </LinkEndImage>
          )}
        </SpanContainer>
      )}
    </React.Fragment>
  );
}

Link.defaultProps = {
  variant: "link",
  text: "CTA Button",
  fontSize: 16,
  fontSizeTablet: 16,
  slotIcon: "start",
  underline: true,
  url: "",
  margin: 0,
  iconSize: "sm",
  underlineStyle: "solid",
  isEndOfLine: false,
};

export default Link;
